import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTopicWeights } from './use-topic-weights';
import { useCommunityWeights } from './use-community-weights';
import { commentCreateSchema, safeParse } from '@/lib/validation/schemas';

export interface Loop {
  id: string;
  user_id: string;
  content: string;
  image_url?: string;
  video_url?: string;
  thumbnail_url?: string;
  view_count: number;
  like_count: number;
  comment_count: number;
  reblog_count?: number;
  quote_count?: number;
  remix_count?: number;
  created_at: string;
  updated_at: string;
  edited_at?: string;
  is_active: boolean;
  is_artstream?: boolean;
  is_mature?: boolean; // Mature/NSFW content flag
  user: {
    username: string;
    display_name: string;
    edited_at?: string;
    profile_image_url?: string;
    tag_code?: string;
    tag_emote?: string;
    community_tag_id?: string;
    community_tag?: string;
    is_admin?: boolean;
    is_partner?: boolean;
  };
  is_liked?: boolean;
  is_viewed?: boolean;
  // Mixed-mode personalization annotations
  relevance_topic?: string;
  relevance_scores?: { recency: number; popularity: number; affinity: number; topic?: string };
  // Semantic topics attached to this loop (from loops_topics)
  topics_sem?: string[];
  // Primary community this loop belongs to
  primary_community?: {
    name: string;
    display_name: string;
    icon?: string;
    color?: string;
    gradient?: string;
  };
  moderation_debug?: {
    adultScore?: number;
    racyScore?: number;
    thresholds?: {
      adult?: number;
      racy?: number;
    };
  } | null;
}

export interface LoopComment {
  id: string;
  loop_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user: {
    username: string;
    display_name: string;
    profile_image_url?: string;
    tag_code?: string;
    tag_emote?: string;
    community_tag_id?: string;
    community_tag?: string;
    is_admin?: boolean;
    is_partner?: boolean;
  };
}

const LOOPS_PER_PAGE = 10;
const CHRONO_CHUNK_SIZE = 1;
const RANDOM_CHUNK_SIZE = 2;
const WEIGHTED_CHUNK_SIZE = 2;

export function useLoops(orderType: 'chronological' | 'mixed' = 'chronological', options?: { onlyArtstream?: boolean; communityName?: string }) {
  const { user } = useAuth();
  const { weights } = useTopicWeights();
  const { data: communityWeights } = useCommunityWeights();
  const [refreshToken] = useState(() => Date.now().toString(36));
  
  // Merge topic weights and community weights
  const weightsMap = new Map<string, number>((weights || []).map(w => [w.topic.toLowerCase(), w.weight]));
  
  // Add community weights to the weights map
  if (communityWeights) {
    for (const [community, weight] of communityWeights.entries()) {
      const existingWeight = weightsMap.get(community) || 0;
      // Blend community weight with existing topic weight
      weightsMap.set(community, Math.min(1, existingWeight * 0.5 + weight * 0.5));
    }
  }
  
  return useInfiniteQuery({
    queryKey: ['loops', orderType, user?.id, options?.communityName],
    queryFn: async ({ pageParam = 0 }) => {
      // Fetch loops without relying on DB relationships (robust across environments)
      // Note: We don't filter is_mature here - mature content will be blurred in UI based on user preference
      let query = supabase
        .from('loops')
        .select('*')
        .eq('is_active', true)
        .in('moderation_status', ['approved', 'flagged']) // Only show moderated content
        .range(pageParam * LOOPS_PER_PAGE, (pageParam + 1) * LOOPS_PER_PAGE - 1);
      
      if (options?.onlyArtstream) {
        query = query.eq('is_artstream', true);
      }
      
      if (options?.communityName) {
        // First get the community ID
        const { data: communityData } = await supabase
          .from('communities')
          .select('id')
          .eq('name', options.communityName)
          .maybeSingle();
          
        if (communityData) {
          // Get loop IDs from loop_communities relationship
          const { data: communityLoopsData } = await supabase
            .from('loop_communities')
            .select('loop_id')
            .eq('community_id', communityData.id)
            .gte('confidence', 0.5); // Only include high-confidence matches
            
          if (communityLoopsData && communityLoopsData.length > 0) {
            const loopIds = communityLoopsData.map(cl => cl.loop_id);
            query = query.in('id', loopIds);
          } else {
            // No loops found for this community, return empty results
            return [];
          }
        } else {
          // Community not found, return empty results
          return [];
        }
      }
      
      // Apply base ordering based on type (DB-level)
      if (orderType === 'chronological') {
        query = query.order('created_at', { ascending: false });
      } else {
        // Mixed: start with recency at DB-level to reduce cold content, then re-rank client-side
        query = query.order('created_at', { ascending: false });
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // Build user profile map without FK-based join
      let profilesMap = new Map<string, { username: string; display_name: string; profile_image_url?: string; tag_code?: string; tag_emote?: string; community_tag_id?: string; community_tag?: string; is_admin?: boolean; is_partner?: boolean }>();
      if (data && data.length > 0) {
        const userIds = Array.from(new Set((data as any[]).map((l) => l.user_id).filter(Boolean)));
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('public_profiles')
            .select('id, username, display_name, profile_image_url, tag_code, tag_emote, community_tag_id, community_tag, is_admin, is_partner')
            .in('id', userIds);
          if (!profilesError && profilesData) {
            for (const p of profilesData as any[]) {
              profilesMap.set(p.id, {
                username: p.username,
                display_name: p.display_name,
                profile_image_url: p.profile_image_url,
                tag_code: p.tag_code,
                tag_emote: p.tag_emote,
                community_tag_id: p.community_tag_id,
                community_tag: p.community_tag,
                is_admin: (p as any).is_admin,
                is_partner: (p as any).is_partner,
              });
            }
          } else {
            console.warn('useLoops: failed to fetch profiles, proceeding without user info', profilesError);
          }
        }
      }

      // Fetch semantic topics for these loops
      const loopIds = (data as any[]).map(l => l.id);
      let topicsByLoop = new Map<string, string[]>();
      if (loopIds.length > 0) {
        const { data: topicsData, error: topicsError } = await supabase
          .from('loops_topics')
          .select('loop_id, topic, source')
          .in('loop_id', loopIds);
        if (!topicsError && topicsData) {
          for (const row of topicsData as any[]) {
            if (row.source !== 'semantic') continue;
            const arr = topicsByLoop.get(row.loop_id) || [];
            if (!arr.includes(row.topic)) arr.push(row.topic);
            topicsByLoop.set(row.loop_id, arr);
          }
        } else if (topicsError) {
          console.warn('useLoops: failed to fetch loops_topics (semantic)', topicsError);
        }
      }

      // Fetch primary community for these loops
      let communityByLoop = new Map<string, any>();
      if (loopIds.length > 0) {
        const { data: communitiesData, error: communitiesError } = await supabase
          .from('loop_communities')
          .select(`
            loop_id,
            confidence,
            community:communities(name, display_name, icon, color, gradient)
          `)
          .in('loop_id', loopIds)
          .gte('confidence', 0.5)
          .order('confidence', { ascending: false });
        
        if (!communitiesError && communitiesData) {
          // Group by loop_id and take the highest confidence community
          for (const row of communitiesData as any[]) {
            if (!communityByLoop.has(row.loop_id) && row.community) {
              communityByLoop.set(row.loop_id, row.community);
            }
          }
        } else if (communitiesError) {
          console.warn('useLoops: failed to fetch loop_communities', communitiesError);
        }
      }

      // Check likes and views for authenticated user
      if (user && data) {
        const [likesData, viewsData] = await Promise.all([
          supabase.from('loops_likes')
            .select('loop_id')
            .eq('user_id', user.id)
            .in('loop_id', loopIds),
          supabase.from('loops_views')
            .select('loop_id')
            .eq('user_id', user.id)
            .in('loop_id', loopIds)
        ]);
        
        const likedLoops = new Set(likesData.data?.map(like => like.loop_id) || []);
        const viewedLoops = new Set(viewsData.data?.map(view => view.loop_id) || []);
        
        const enriched = (data as any[]).map(loop => ({
          ...loop,
          user: profilesMap.get(loop.user_id) || {
            username: 'unknown',
            display_name: 'Unknown',
            profile_image_url: undefined,
            tag_code: undefined,
            tag_emote: undefined,
          },
          is_liked: likedLoops.has(loop.id),
          is_viewed: viewedLoops.has(loop.id),
          topics_sem: topicsByLoop.get(loop.id) || [],
          primary_community: communityByLoop.get(loop.id),
          moderation_debug: loop.moderation_result
        }));
        if (orderType === 'mixed') {
          const seedKey = `${user?.id ?? 'anon'}|${refreshToken}|${(enriched as any[]).map(item => item.id).join('|')}`;
          return composeHybridMixedOrder(enriched, weightsMap, seedKey);
        }
        return enriched;
      }
      
      const enrichedAnon = (data as any[]).map(loop => ({
        ...loop,
        user: profilesMap.get(loop.user_id) || {
          username: 'unknown',
          display_name: 'Unknown',
          profile_image_url: undefined,
          tag_code: undefined,
          tag_emote: undefined,
        },
        topics_sem: topicsByLoop.get(loop.id) || [],
        primary_community: communityByLoop.get(loop.id),
        moderation_debug: loop.moderation_result
      }));
      if (orderType === 'mixed') {
        const seedKey = `anon|${refreshToken}|${(enrichedAnon as any[]).map(item => item.id).join('|')}`;
        return composeHybridMixedOrder(enrichedAnon as any[], weightsMap, seedKey);
      }
      return enrichedAnon;
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length === LOOPS_PER_PAGE ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// --- Helpers for mixed ranking ---
function extractHashtags(text?: string): string[] {
  if (!text) return [];
  const tags = text.match(/#\w+/g);
  return tags ? tags.map(t => t.slice(1).toLowerCase()) : [];
}

function recencyScore(created_at: string): number {
  const now = Date.now();
  const t = new Date(created_at).getTime();
  const hours = Math.max(0, (now - t) / (1000 * 60 * 60));
  // Exponential decay over ~4 days (slower decay)
  const score = Math.exp(-hours / 96);
  return clamp01(score);
}

function popularityScore(loop: any): number {
  const likes = loop.like_count || 0;
  const views = loop.view_count || 0;
  const reblog = loop.reblog_count || 0;
  const quote = loop.quote_count || 0;
  // Weighted sum then log scale
  const raw = likes * 1 + views * 0.05 + reblog * 2 + quote * 1.5;
  const score = Math.log10(1 + raw) / 3; // keep 0..~1 range for typical values
  return clamp01(score);
}

function topicAffinityScore(loop: any, weights: Map<string, number>): number {
  if (weights.size === 0) return 0;
  const tags = extractHashtags(loop.content);
  const sem = (loop.topics_sem as string[] | undefined) || [];

  const avg = (arr: string[]) => {
    if (!arr.length) return 0;
    let s = 0;
    for (const t of arr) {
      const w = weights.get(t.toLowerCase());
      if (typeof w === 'number') s += w;
    }
    return s / arr.length;
  };

  const affinity_sem = avg(sem);
  const affinity_hash = avg(tags);
  // Favor semantic topics over hashtags
  const blended = 0.7 * affinity_sem + 0.3 * affinity_hash;
  return clamp01(blended);
}

interface MixedScoreEntry {
  item: any;
  score: number;
}

interface ScoreWeightConfig {
  recency: number;
  popularity: number;
  affinity: number;
  jitter: number;
}

const DEFAULT_SCORE_WEIGHTS: ScoreWeightConfig = {
  recency: 0.45,
  popularity: 0.35,
  affinity: 0.20,
  jitter: 0,
};

function computeMixedScores(
  list: any[],
  weights: Map<string, number>,
  options?: { scoreWeights?: Partial<ScoreWeightConfig>; seedKey?: string }
): MixedScoreEntry[] {
  const config = { ...DEFAULT_SCORE_WEIGHTS, ...(options?.scoreWeights ?? {}) };
  const total = config.recency + config.popularity + config.affinity;
  const recencyWeight = total === 0 ? 0 : config.recency / total;
  const popularityWeight = total === 0 ? 0 : config.popularity / total;
  const affinityWeight = total === 0 ? 0 : config.affinity / total;
  const jitterWeight = config.jitter;
  const jitterSeedBase = options?.seedKey ?? 'default';

  return list.map(item => {
    const r = recencyScore(item.created_at);
    const p = popularityScore(item);
    const a = topicAffinityScore(item, weights);
    const tags = extractHashtags(item.content);
    const sem = (item.topics_sem as string[] | undefined) || [];
    let primary: string | undefined;
    for (const t of sem) {
      if (weights.has(t.toLowerCase())) { primary = t; break; }
    }
    if (!primary && sem.length) primary = sem[0];
    if (!primary) {
      for (const t of tags) {
        if (weights.has(t.toLowerCase())) { primary = t; break; }
      }
    }
    if (!primary) primary = tags[0];
    const jitter = jitterWeight
      ? deterministicNoise(`${jitterSeedBase}|${item.id}`) * jitterWeight
      : 0;
    const score = recencyWeight * r + popularityWeight * p + affinityWeight * a + jitter;
    const annotated = {
      ...item,
      relevance_topic: primary,
      relevance_scores: { recency: r, popularity: p, affinity: a, topic: primary }
    };
    return { item: annotated, score };
  });
}

function reRankMixed(list: any[], weights: Map<string, number>): any[] {
  const scored = computeMixedScores(list, weights);
  scored.sort((a, b) => b.score - a.score);
  return scored.map(s => s.item);
}

function composeHybridMixedOrder(list: any[], weights: Map<string, number>, seedKey: string): any[] {
  if (list.length === 0) {
    return [];
  }

  const cycleSize = CHRONO_CHUNK_SIZE + RANDOM_CHUNK_SIZE + WEIGHTED_CHUNK_SIZE;
  if (list.length <= cycleSize) {
    return reRankMixed(list, weights);
  }

  const baseScored = computeMixedScores(list, weights, { seedKey: `base|${seedKey}` });
  const idToEntry = new Map<string, MixedScoreEntry>();
  for (const entry of baseScored) {
    if (entry?.item?.id) {
      idToEntry.set(entry.item.id, entry);
    }
  }

  const chronologicalOrder = list.map(item => item.id);
  const randomOrder = stableShuffle(chronologicalOrder, `random|${seedKey}`);
  const weightedOrder = computeMixedScores(list, weights, {
    scoreWeights: { recency: 0.25, popularity: 0.4, affinity: 0.35, jitter: 0.03 },
    seedKey: `weighted|${seedKey}`,
  })
    .sort((a, b) => b.score - a.score)
    .map(entry => entry.item.id);

  const used = new Set<string>();
  let chronoIndex = 0;
  let randomIndex = 0;
  let weightedIndex = 0;
  const result: any[] = [];

  while (result.length < list.length) {
    let addedThisCycle = 0;

    // Chronological pick(s)
    for (let i = 0; i < CHRONO_CHUNK_SIZE && result.length < list.length; i++) {
      const { id, nextIndex } = takeNextAvailable(chronologicalOrder, chronoIndex, used);
      chronoIndex = nextIndex;
      if (!id) break;
      const item = idToEntry.get(id)?.item ?? list.find(loop => loop.id === id);
      if (item) {
        result.push(item);
        used.add(id);
        addedThisCycle++;
      }
    }

    // Random pick(s)
    for (let i = 0; i < RANDOM_CHUNK_SIZE && result.length < list.length; i++) {
      const { id, nextIndex } = takeNextAvailable(randomOrder, randomIndex, used);
      randomIndex = nextIndex;
      if (!id) break;
      const item = idToEntry.get(id)?.item ?? list.find(loop => loop.id === id);
      if (item) {
        result.push(item);
        used.add(id);
        addedThisCycle++;
      }
    }

    // Weighted pick(s)
    for (let i = 0; i < WEIGHTED_CHUNK_SIZE && result.length < list.length; i++) {
      const { id, nextIndex } = takeNextAvailable(weightedOrder, weightedIndex, used);
      weightedIndex = nextIndex;
      if (!id) break;
      const item = idToEntry.get(id)?.item ?? list.find(loop => loop.id === id);
      if (item) {
        result.push(item);
        used.add(id);
        addedThisCycle++;
      }
    }

    if (addedThisCycle === 0) {
      break;
    }
  }

  if (result.length < list.length) {
    for (const id of chronologicalOrder) {
      if (used.has(id)) continue;
      const item = idToEntry.get(id)?.item ?? list.find(loop => loop.id === id);
      if (item) {
        result.push(item);
        used.add(id);
      }
      if (result.length === list.length) break;
    }
  }

  return result;
}

function takeNextAvailable(order: string[], startIndex: number, used: Set<string>): { id?: string; nextIndex: number } {
  let idx = startIndex;
  while (idx < order.length) {
    const candidate = order[idx++];
    if (!used.has(candidate)) {
      return { id: candidate, nextIndex: idx };
    }
  }
  return { nextIndex: order.length };
}

function stableShuffle<T>(items: T[], seedKey: string): T[] {
  const arr = [...items];
  let seed = hashStringToSeed(seedKey);
  if (seed === 0) {
    seed = 1;
  }
  for (let i = arr.length - 1; i > 0; i--) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const rand = seed / 0xffffffff;
    const j = Math.floor(rand * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function hashStringToSeed(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
}

function deterministicNoise(key: string): number {
  const seed = hashStringToSeed(key);
  return (seed / 0xffffffff) * 2 - 1;
}

function clamp01(n: number): number { return Math.max(0, Math.min(1, n)); }

export function useLoopComments(loopId: string) {
  return useQuery({
    queryKey: ['loop-comments', loopId],
    queryFn: async () => {
      // Fetch comments without relying on DB relationships
      const { data, error } = await supabase
        .from('loops_comments')
        .select('*')
        .eq('loop_id', loopId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      const comments = (data as any[]) || [];
      const userIds = Array.from(new Set(comments.map((c) => c.user_id).filter(Boolean)));

      let profilesMap = new Map<string, { 
        username: string; 
        display_name: string; 
        profile_image_url?: string; 
        tag_code?: string; 
        tag_emote?: string; 
        community_tag_id?: string; 
        community_tag?: string; 
        is_admin?: boolean; 
        is_partner?: boolean;
      }>();
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('public_profiles')
          .select('id, username, display_name, profile_image_url, tag_code, tag_emote, community_tag_id, community_tag, is_admin, is_partner')
          .in('id', userIds);
        if (!profilesError && profilesData) {
          for (const p of profilesData as any[]) {
            profilesMap.set(p.id, {
              username: p.username,
              display_name: p.display_name,
              profile_image_url: p.profile_image_url,
              tag_code: p.tag_code,
              tag_emote: p.tag_emote,
              community_tag_id: p.community_tag_id,
              community_tag: p.community_tag,
              is_admin: (p as any).is_admin,
              is_partner: (p as any).is_partner,
            });
          }
        } else {
          console.warn('useLoopComments: failed to fetch profiles, proceeding without user info', profilesError);
        }
      }

      return comments.map((c) => ({
        ...c,
        user: profilesMap.get(c.user_id) || {
          username: 'unknown',
          display_name: 'Unknown',
          profile_image_url: undefined,
          tag_code: undefined,
          tag_emote: undefined,
        },
      }));
    },
    enabled: !!loopId,
  });
}

export function useLoopActions() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { weights, upsertTopicWeights } = useTopicWeights();
  
  const likeLoop = useMutation({
    mutationFn: async (loopId: string) => {
      if (!user) throw new Error('Authentication required');
      
      const { error } = await supabase
        .from('loops_likes')
        .insert({ loop_id: loopId, user_id: user.id });
      
      if (error) throw error;
    },
    onSuccess: async (_, loopId) => {
      queryClient.invalidateQueries({ queryKey: ['loops'] });
      queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] });
      toast({ title: 'Loop liked!' });
      // Increment topic weights slightly based on loop content hashtags
      try {
        const { data } = await supabase.from('loops').select('content').eq('id', loopId).maybeSingle();
        const tags = extractHashtags(data?.content);
        if (tags.length) {
          const current = new Map<string, number>((weights || []).map(w => [w.topic, w.weight]));
          const updates = tags.map(t => ({ topic: t, weight: clamp01((current.get(t) ?? 0) + 0.05) }));
          upsertTopicWeights(updates);
        }
      } catch {}
    },
    onError: (error) => {
      console.error('Error liking loop:', error);
      toast({ 
        title: 'Error liking loop', 
        description: 'Please try again',
        variant: 'destructive' 
      });
    }
  });
  
  const unlikeLoop = useMutation({
    mutationFn: async (loopId: string) => {
      if (!user) throw new Error('Authentication required');
      
      const { error } = await supabase
        .from('loops_likes')
        .delete()
        .eq('loop_id', loopId)
        .eq('user_id', user.id);
      
      if (error) throw error;
    },
    onSuccess: (_, loopId) => {
      queryClient.invalidateQueries({ queryKey: ['loops'] });
      queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] });
    },
    onError: (error) => {
      console.error('Error unliking loop:', error);
      toast({ 
        title: 'Error unliking loop', 
        description: 'Please try again',
        variant: 'destructive' 
      });
    }
  });
  
  const viewLoop = useMutation({
    mutationFn: async (loopId: string) => {
      const { error } = await supabase
        .from('loops_views')
        .insert({ 
          loop_id: loopId, 
          user_id: user?.id || null 
        });
      
      if (error && !error.message.includes('duplicate key')) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loops'] });
    },
    onError: (error) => {
      console.error('Error tracking view:', error);
    }
  });
  
  const addComment = useMutation({
    mutationFn: async ({ loopId, content }: { loopId: string; content: string }) => {
      if (!user) throw new Error('Authentication required');
      
      // SECURITY: Validate comment data
      const validationResult = safeParse(commentCreateSchema, {
        content: content,
        bulletin_id: loopId, // Using bulletin_id field for loop_id
      });
      
      if (!validationResult.success) {
        const error = validationResult as { success: false; error: string };
        throw new Error(error.error);
      }
      
      const { error } = await supabase
        .from('loops_comments')
        .insert({
          loop_id: loopId,
          user_id: user.id,
          content: content.trim()
        });
      
      if (error) throw error;

      // Create notification for the loop author (if not commenting on own loop)
      try {
        const { data: loopData } = await supabase
          .from('loops')
          .select('user_id, image_url, thumbnail_url')
          .eq('id', loopId)
          .single();

        if (loopData && loopData.user_id !== user.id) {
          await supabase
            .from('notifications')
            .insert({
              user_id: loopData.user_id,
              actor_id: user.id,
              type: 'comment',
              loop_id: loopId,
              image_url: loopData.thumbnail_url || loopData.image_url
            });
        }
      } catch (notificationError) {
        console.error('Error creating comment notification:', notificationError);
        // Don't fail the comment creation if notification fails
      }
    },
    onSuccess: async (_, { loopId }) => {
      queryClient.invalidateQueries({ queryKey: ['loop-comments', loopId] });
      queryClient.invalidateQueries({ queryKey: ['loops'] });
      // Stronger boost for commenting
      try {
        const { data } = await supabase.from('loops').select('content').eq('id', loopId).maybeSingle();
        const tags = extractHashtags(data?.content);
        if (tags.length) {
          const current = new Map<string, number>((weights || []).map(w => [w.topic, w.weight]));
          const updates = tags.map(t => ({ topic: t, weight: clamp01((current.get(t) ?? 0) + 0.08) }));
          upsertTopicWeights(updates);
        }
      } catch {}
    },
    onError: (error) => {
      console.error('Error adding comment:', error);
      toast({ 
        title: 'Error adding comment', 
        description: 'Please try again',
        variant: 'destructive' 
      });
    }
  });
  
  // Delete a loop - restricted to post author or admin users
  const deleteLoop = useMutation({
    mutationFn: async ({ loopId, userId }: { loopId: string; userId: string }) => {
      if (!user) throw new Error('Authentication required');
      
      // First check if user is the author or an admin
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .maybeSingle();
      
      if (userError) throw userError;
      
      const isAdmin = userData?.is_admin === true;
      const isAuthor = user.id === userId;
      
      if (!isAdmin && !isAuthor) {
        throw new Error('Permission denied: You must be the author or an admin to delete this post');
      }
      
      // If authorized, delete the loop
      const { error } = await supabase
        .from('loops')
        .delete()
        .eq('id', loopId);
      if (error) throw error;

      // Also delete any linked bulletin to keep feeds consistent (best-effort)
      try {
        await supabase
          .from('bulletins')
          .delete()
          .eq('loop_id', loopId);
      } catch (e) {
        // Non-fatal: if RLS prevents deletion, the primary loop is gone anyway
        console.warn('deleteLoop: failed to delete linked bulletin (non-fatal)', e);
      }
    },
    onSuccess: (_, variables) => {
      // Proactively remove the deleted loop from any cached lists
      try {
        queryClient.setQueriesData({ queryKey: ['loops'] }, (old: any) => {
          if (!old) return old;
          // Support both array list and infinite query shapes
          if (Array.isArray(old)) {
            return old.filter((l: any) => l.id !== variables.loopId);
          }
          if (old?.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any[]) => page.filter((l) => l.id !== variables.loopId))
            };
          }
          return old;
        });
      } catch {}
      queryClient.invalidateQueries({ queryKey: ['loops'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['user-loops-and-reblogs'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'], exact: false }); // Artstream feed
      queryClient.invalidateQueries({ queryKey: ['bulletins'], exact: false }); // Bulletins feed
      queryClient.invalidateQueries({ queryKey: ['loop-communities'], exact: false }); // Community hashtags
      toast({ 
        title: 'Post deleted', 
        description: 'The post has been permanently removed',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting post:', error);
      if (error.message.includes('Permission denied')) {
        toast({ 
          title: 'Permission denied', 
          description: 'You can only delete your own posts or must be an admin',
          variant: 'destructive' 
        });
      } else {
        toast({ 
          title: 'Error deleting post', 
          description: 'Please try again',
          variant: 'destructive' 
        });
      }
    }
  });
  
  return {
    likeLoop: likeLoop.mutate,
    unlikeLoop: unlikeLoop.mutate,
    viewLoop: viewLoop.mutate,
    addComment: addComment.mutate,
    deleteLoop: deleteLoop.mutate,
    isLiking: likeLoop.isPending,
    isUnliking: unlikeLoop.isPending,
    isViewing: viewLoop.isPending,
    isCommenting: addComment.isPending,
    isDeleting: deleteLoop.isPending
  };
}